import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Stethoscope, X, Check, Search } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import ProfessionalBadge from './ProfessionalBadge';

export default function SelectProfessional({ user, onClose }) {
  const [searchQuery, setSearchQuery] = useState('');
  const queryClient = useQueryClient();

  const { data: professionals = [] } = useQuery({
    queryKey: ['verified-professionals'],
    queryFn: async () => {
      const users = await base44.entities.User.list();
      return users.filter(u => u.is_professional && u.professional_verified);
    },
  });

  const { data: myProfessional } = useQuery({
    queryKey: ['my-professional', user?.id],
    queryFn: async () => {
      const relations = await base44.entities.UserProfessionalRelation.filter({ 
        user_id: user?.id,
        status: 'active'
      });
      if (relations.length === 0) return null;
      
      const allUsers = await base44.entities.User.list();
      return allUsers.find(u => u.id === relations[0].professional_id);
    },
    enabled: !!user?.id,
  });

  const selectProfessionalMutation = useMutation({
    mutationFn: async (professionalId) => {
      // Desactivar relación anterior si existe
      const existingRelations = await base44.entities.UserProfessionalRelation.filter({ 
        user_id: user.id,
        status: 'active'
      });
      
      for (const rel of existingRelations) {
        await base44.entities.UserProfessionalRelation.update(rel.id, { status: 'inactive' });
      }

      // Crear nueva relación
      return await base44.entities.UserProfessionalRelation.create({
        user_id: user.id,
        professional_id: professionalId,
        status: 'active',
        share_products: true,
        share_health_data: true
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-professional'] });
      toast.success('Profesional seleccionado exitosamente');
      onClose();
    },
  });

  const removeProfessionalMutation = useMutation({
    mutationFn: async () => {
      const relations = await base44.entities.UserProfessionalRelation.filter({ 
        user_id: user.id,
        status: 'active'
      });
      
      for (const rel of relations) {
        await base44.entities.UserProfessionalRelation.update(rel.id, { status: 'inactive' });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-professional'] });
      toast.success('Relación con profesional removida');
    },
  });

  const filteredProfessionals = professionals.filter(prof => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      prof.full_name?.toLowerCase().includes(query) ||
      prof.professional_type?.toLowerCase().includes(query) ||
      prof.professional_specialty?.toLowerCase().includes(query)
    );
  });

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-hidden shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                <Stethoscope className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">Seleccionar Profesional</h2>
                <p className="text-sm text-gray-500">Comparte tu información de salud</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {myProfessional && (
            <Card className="p-4 bg-blue-50 border-blue-200 mb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {myProfessional.profile_picture ? (
                    <img src={myProfessional.profile_picture} alt={myProfessional.full_name} className="w-10 h-10 rounded-full object-cover" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold">
                      {myProfessional.full_name?.[0]?.toUpperCase()}
                    </div>
                  )}
                  <div>
                    <p className="font-medium text-gray-900">Profesional actual: {myProfessional.full_name}</p>
                    <p className="text-xs text-gray-600">{myProfessional.professional_type}</p>
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => removeProfessionalMutation.mutate()}
                  className="text-red-600 border-red-200 hover:bg-red-50"
                >
                  Remover
                </Button>
              </div>
            </Card>
          )}

          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Buscar por nombre, tipo o especialidad..."
              className="pl-10"
            />
          </div>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          {filteredProfessionals.length === 0 ? (
            <div className="text-center py-8">
              <Stethoscope className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No se encontraron profesionales verificados</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredProfessionals.map((prof) => (
                <Card 
                  key={prof.id} 
                  className={`p-4 hover:shadow-md transition-shadow cursor-pointer ${
                    myProfessional?.id === prof.id ? 'border-blue-500 bg-blue-50' : ''
                  }`}
                  onClick={() => selectProfessionalMutation.mutate(prof.id)}
                >
                  <div className="flex items-start gap-3">
                    {prof.profile_picture ? (
                      <img 
                        src={prof.profile_picture} 
                        alt={prof.full_name}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold">
                        {prof.full_name?.[0]?.toUpperCase()}
                      </div>
                    )}
                    
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-gray-900">{prof.full_name}</h3>
                        <ProfessionalBadge user={prof} size="sm" showLabel={false} />
                      </div>
                      <p className="text-sm text-blue-600">
                        {prof.professional_type === 'nutricionista' ? 'Nutricionista' :
                         prof.professional_type === 'medico' ? 'Médico' :
                         prof.professional_type === 'enfermero' ? 'Enfermero' :
                         prof.professional_type === 'farmaceutico' ? 'Farmacéutico' :
                         'Profesional de salud'}
                        {prof.professional_specialty && ` - ${prof.professional_specialty}`}
                      </p>
                      {prof.professional_license && (
                        <p className="text-xs text-gray-500 mt-1">MP: {prof.professional_license}</p>
                      )}
                      {prof.professional_bio && (
                        <p className="text-sm text-gray-600 mt-2 line-clamp-2">{prof.professional_bio}</p>
                      )}
                    </div>

                    {myProfessional?.id === prof.id && (
                      <Check className="w-5 h-5 text-blue-600" />
                    )}
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}